// AI Transform Service
// Handles AI image transformation API calls

import { db } from '../firebase-config';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';

/**
 * Transform property image using AI
 * @param {File} imageFile - Original image file
 * @param {string} style - Selected style (Modern, Luxury, Minimal, Airbnb, Classic)
 * @param {string} roomType - Type of room (living room, bedroom, kitchen, etc.)
 * @returns {Promise<{transformedImageUrl: string, duration: number}>}
 */
export const transformPropertyImage = async (imageFile, style, roomType = 'living room') => {
  try {
    const startTime = Date.now();
    
    // Upload original image to Firebase Storage first
    const originalImageUrl = await uploadImageToStorage(imageFile);
    
    // Call backend API for AI transformation
    const response = await fetch('/api/ai-transform', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imageUrl: originalImageUrl,
        style: style.toLowerCase(),
        roomType: roomType.toLowerCase(),
        prompt: generateStylePrompt(style, roomType)
      })
    });

    if (!response.ok) {
      throw new Error('AI transformation failed');
    }

    const data = await response.json();
    const duration = Date.now() - startTime;

    return {
      transformedImageUrl: data.transformedImageUrl,
      duration
    };
  } catch (error) {
    console.error('Error transforming image:', error);
    
    // Fallback: Return mock transformation (for development)
    return {
      transformedImageUrl: URL.createObjectURL(imageFile), // Mock: same image
      duration: 2000,
      isMock: true
    };
  }
};

/**
 * Generate style-specific prompt for AI
 */
const generateStylePrompt = (style, roomType) => {
  const basePrompt = `Transform this ${roomType} interior into a ${style} style.`;
  
  const stylePrompts = {
    modern: `${basePrompt} Clean lines, neutral colors, minimal furniture, bright natural lighting, contemporary design. Preserve room geometry and window placement. Keep realistic proportions.`,
    
    luxury: `${basePrompt} Elegant premium furniture, marble accents, warm luxury materials, refined lighting, upscale details. Preserve room geometry and window placement. Keep realistic and tasteful.`,
    
    minimal: `${basePrompt} Simplified layout, low clutter, light neutral tones, functional elegance, airy space. Preserve room geometry and window placement. Keep realistic and clean.`,
    
    airbnb: `${basePrompt} Cozy modern hospitality vibe, warm inviting feel, photogenic layout, comfortable textures. Preserve room geometry and window placement. Keep realistic and welcoming.`,
    
    classic: `${basePrompt} Timeless elegant design, traditional furniture, warm wood tones, classic details. Preserve room geometry and window placement. Keep realistic and sophisticated.`
  };

  return stylePrompts[style.toLowerCase()] || stylePrompts.modern;
};

/**
 * Upload image to Firebase Storage
 */
const uploadImageToStorage = async (imageFile) => {
  // TODO: Implement Firebase Storage upload
  // For now, return mock URL
  return URL.createObjectURL(imageFile);
};

/**
 * Save AI property transformation to Firestore
 */
export const saveAIProperty = async (userId, propertyData, transformationData) => {
  try {
    const propertyRef = await addDoc(collection(db, 'ai_properties'), {
      userId,
      propertyData: {
        address: propertyData.address,
        area_m2: parseInt(propertyData.area_m2) || 0,
        rooms: parseInt(propertyData.rooms) || 0,
        price: parseInt(propertyData.price) || 0
      },
      transformation: {
        selectedStyle: transformationData.selectedStyle,
        originalImages: transformationData.originalImages || [],
        transformedImages: transformationData.transformedImages || [],
        priceImpact: transformationData.priceImpact
      },
      status: 'draft', // draft, published, archived
      tier: transformationData.tier || 'free', // free, pro, premium
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return propertyRef.id;
  } catch (error) {
    console.error('Error saving AI property:', error);
    throw error;
  }
};

/**
 * Update AI property
 */
export const updateAIProperty = async (propertyId, updates) => {
  try {
    const propertyRef = doc(db, 'ai_properties', propertyId);
    await updateDoc(propertyRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating AI property:', error);
    throw error;
  }
};

/**
 * Get user's AI property quota
 */
export const getUserQuota = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        tier: 'free',
        imagesRemaining: 1,
        stylesAvailable: ['modern'],
        maxImages: 1
      };
    }

    const userData = userDoc.data();
    const tier = userData.aiTier || 'free';

    const quotas = {
      free: {
        tier: 'free',
        imagesRemaining: 1 - (userData.aiImagesUsed || 0),
        stylesAvailable: ['modern'],
        maxImages: 1
      },
      pro: {
        tier: 'pro',
        imagesRemaining: 5 - (userData.aiImagesUsed || 0),
        stylesAvailable: ['modern', 'luxury', 'minimal', 'airbnb', 'classic'],
        maxImages: 5
      },
      premium: {
        tier: 'premium',
        imagesRemaining: 10 - (userData.aiImagesUsed || 0),
        stylesAvailable: ['modern', 'luxury', 'minimal', 'airbnb', 'classic'],
        maxImages: 10,
        features: ['marketing_package', 'boost', 'ai_copywriting', 'consultation']
      }
    };

    return quotas[tier] || quotas.free;
  } catch (error) {
    console.error('Error getting user quota:', error);
    return {
      tier: 'free',
      imagesRemaining: 1,
      stylesAvailable: ['modern'],
      maxImages: 1
    };
  }
};

/**
 * Check if user can use AI transform
 */
export const canUseAITransform = async (userId, imageCount = 1) => {
  const quota = await getUserQuota(userId);
  return quota.imagesRemaining >= imageCount;
};

/**
 * Increment user's AI usage
 */
export const incrementAIUsage = async (userId, imageCount = 1) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const currentUsage = userDoc.data().aiImagesUsed || 0;
      await updateDoc(userRef, {
        aiImagesUsed: currentUsage + imageCount,
        lastAIUsage: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error incrementing AI usage:', error);
  }
};

/**
 * Calculate price impact based on property data and style
 */
export const calculatePriceImpact = (propertyData, style = 'Modern') => {
  const basePrice = parseFloat(propertyData.price) || 0;
  
  // Style-specific price impact percentages
  const styleImpact = {
    'Modern': 0.097,      // +9.7%
    'Luxury': 0.125,      // +12.5%
    'Minimal': 0.085,     // +8.5%
    'Airbnb': 0.11,       // +11%
    'Classic': 0.075      // +7.5%
  };
  
  const impactPercent = styleImpact[style] || 0.097;
  const priceIncrease = basePrice * impactPercent;
  const newPrice = basePrice + priceIncrease;
  
  return {
    originalPrice: basePrice,
    newPrice: newPrice,
    increase: priceIncrease,
    increasePercent: impactPercent * 100,
    style: style
  };
};

/**
 * Calculate Deal Score (1-100) based on profit potential
 * Higher score = better investment opportunity
 */
export const calculateDealScore = (propertyData, priceImpact) => {
  const profit = priceImpact.increase;
  const totalInvestment = priceImpact.originalPrice;
  
  if (!totalInvestment || totalInvestment === 0) return 0;
  
  // ROI percentage
  const roi = (profit / totalInvestment) * 100;
  
  // Score calculation (0-100)
  // 5% ROI = 50 points, 15% ROI = 100 points
  let score = (roi / 15) * 100;
  
  // Cap at 100
  score = Math.min(100, Math.max(0, score));
  
  return Math.round(score);
};

/**
 * Get badge type based on deal score and profit
 */
export const getDealBadge = (dealScore, profit) => {
  const profitMillions = profit / 1000000; // Convert to millions
  
  if (dealScore >= 80 && profitMillions >= 10) {
    return {
      type: 'HOT',
      label: '🔥 HOT DEAL',
      color: 'bg-gradient-to-r from-red-600 to-orange-600',
      textColor: 'text-white'
    };
  }
  
  if (dealScore >= 70 || profitMillions >= 8) {
    return {
      type: 'VIP',
      label: '💎 VIP',
      color: 'bg-gradient-to-r from-purple-600 to-pink-600',
      textColor: 'text-white'
    };
  }
  
  if (dealScore >= 60) {
    return {
      type: 'GOOD',
      label: '⚡ JÓ DEAL',
      color: 'bg-gradient-to-r from-green-600 to-emerald-600',
      textColor: 'text-white'
    };
  }
  
  return null;
};

/**
 * Get AI suggestions based on property data
 */
export const getAISuggestions = (propertyData, priceImpact) => {
  const suggestions = [
    {
      id: 'painting',
      icon: '🎨',
      title: 'Festés',
      impact: '+1.5M érték',
      description: 'Friss festés modern színekkel',
      estimatedCost: 500000,
      estimatedValue: 1500000
    },
    {
      id: 'furniture',
      icon: '🛋️',
      title: 'Bútorozás',
      impact: 'Gyorsabb eladás',
      description: 'Staging bútorokkal',
      estimatedCost: 800000,
      estimatedValue: 0,
      timeReduction: '30%'
    },
    {
      id: 'photo',
      icon: '📸',
      title: 'Profi fotó',
      impact: 'Több érdeklődő',
      description: 'Professzionális ingatlanfotózás',
      estimatedCost: 50000,
      estimatedValue: 0,
      leadIncrease: '40%'
    }
  ];

  // Add conditional suggestions based on property data
  if (parseInt(propertyData.area_m2) > 80) {
    suggestions.push({
      id: 'renovation',
      icon: '🔨',
      title: 'Felújítás',
      impact: '+3M érték',
      description: 'Részleges felújítás',
      estimatedCost: 2000000,
      estimatedValue: 3000000
    });
  }

  return suggestions;
};
